/**
 * DeviceScanPanel: the Discover step. One decision: which device to onboard.
 *
 * Shown up front: the gateway (collapsed to one line with "Change" when the
 * pick is obvious: the only gateway, or the only online multi-protocol one)
 * and a big Scan button with a one-line summary of what it will sweep. The
 * rest is revealed on demand:
 *
 * - "Scan options" (collapsed, its summary line beside it): the protocols to
 *   probe (chips from `GET /v1/things/drivers?gateway_id=`), where to scan as a
 *   radio choice between the gateway's own networks (from its birth
 *   `capabilities.networks`, several can be ticked, scanned one after another)
 *   and a custom range, with the host count and time inline, and a Ports field
 *   per probed protocol, prefilled with the ports the gateway probes by default
 *   (lib/iot/scan-ports; only a changed list is sent, as the backend's `ports`).
 * - While scanning: progress per network (done / scanning / waiting) and a
 *   Cancel that abandons the scan (the gateway finishes its current sweep).
 * - Results: one card per device (a radio), grouped by network; the picked
 *   card offers "Use this device", which picks it and goes to the next step.
 * - "Add a device by hand" (a link that expands): protocol, address (+ port,
 *   unit id for Modbus), with a non-blocking warning when the address is on
 *   none of the gateway's networks.
 *
 * The request is unchanged: without a drivers list (an older server) or with
 * only Modbus TCP ticked, and every port left at its default, it is exactly
 * the pre-multi-protocol body. Every card
 * is a real gateway's answer; without the live API, or with no gateway, the
 * panel says why it cannot scan instead of listing example devices.
 */

import { useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, CircleStop, Loader2, Plus, Radar, Search, TriangleAlert } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { Disclosure } from "@/lib/ui/disclosure";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { NativeSelect } from "@/lib/ui/native-select";
import { RadioCard, RadioCardGroup } from "@/lib/ui/radio-cards";
import { Skeleton } from "@/lib/ui/skeleton";
import { EmptyState } from "@/lib/ui/empty-state";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";
import { isLiveApi, statusOf } from "@/lib/api";
import {
  deviceAddressLabel as addressLabel,
  deviceKey,
  gateway,
  manualDevice,
  scanErrorMessage,
  type DiscoveredDevice,
} from "@/lib/gateway-client";
import {
  DRIVER,
  discoverChips,
  discoverDriversParam,
  driverDefaultHost,
  driverDefaultPort,
  driverLabel,
  identitySummary,
  isModbusDriver,
  manualHostAfterDriverChange,
} from "@/lib/iot/drivers";
import { readBirth } from "@/lib/iot/gateway-birth";
import { gatewayCardLine, type GatewayCard } from "@/lib/iot/gateway-cards";
import {
  SWEEP_BUDGET_S,
  checkScanTarget,
  defaultNetworkId,
  manualAddressWarning,
  networkOptions,
  scanEstimate,
  scanOptionsSummary,
  scanTargets,
  shortNetworkLabel,
} from "@/lib/iot/scan-networks";
import { checkScanPorts, defaultScanPorts, discoverPortsParam, formatScanPorts } from "@/lib/iot/scan-ports";
import { useGatewayDrivers, useGatewayThing } from "./gateway-queries";
import { DeviceErrorText } from "@/components/connect/DeviceErrorText";

export interface DeviceScanPanelProps {
  /** The gateways to scan from, sorted (online and multi-protocol first). */
  cards: GatewayCard[];
  gatewaysLoading: boolean;
  gatewayId: string | null;
  /** The pre-selection is the obvious one: show it collapsed with "Change". */
  gatewayObvious: boolean;
  onGatewayId: (id: string) => void;
  selected: DiscoveredDevice | null;
  onPick: (device: DiscoveredDevice) => void;
  /** "Use this device": pick it and go on to the next step. */
  onUse: (device: DiscoveredDevice) => void;
}

/** One scanned range and what it found. */
interface ScanGroup {
  /** The range sent ("" = the gateway's own network). */
  cidr: string;
  /** How the picker named it ("eth0 · 10.2.10.0/24 · internet"), else the range. */
  label: string;
  devices: DiscoveredDevice[];
  partial: boolean;
  error?: string;
}

interface ScanTarget {
  cidr: string;
  label: string;
}

/** Where to scan when the gateway reported its networks. */
type WhereMode = "networks" | "custom";

export function DeviceScanPanel({
  cards,
  gatewaysLoading,
  gatewayId,
  gatewayObvious,
  onGatewayId,
  selected,
  onPick,
  onUse,
}: DeviceScanPanelProps) {
  const rangeId = useId();

  const thingQ = useGatewayThing(gatewayId);
  const driversQ = useGatewayDrivers(gatewayId);
  const drivers = driversQ.data?.drivers;
  const birth = useMemo(() => readBirth(thingQ.data), [thingQ.data]);
  const options = useMemo(() => networkOptions(birth.networks), [birth.networks]);
  const hasNetworks = birth.networks !== null && options.length > 0;
  const chips = useMemo(() => discoverChips(drivers), [drivers]);

  // Picks default from the gateway's data until the operator changes them; a
  // gateway change resets them (see changeGateway).
  const [pickedDrivers, setPickedDrivers] = useState<Set<string> | null>(null);
  const [pickedNets, setPickedNets] = useState<Set<string> | null>(null);
  const [where, setWhere] = useState<WhereMode>("networks");
  const [range, setRange] = useState("");
  // Each driver's Ports field as typed; a driver not in here shows its defaults.
  const [portTexts, setPortTexts] = useState<Record<string, string>>({});
  const [optionsOpen, setOptionsOpen] = useState(false);

  const effectiveDrivers = useMemo(
    () => pickedDrivers ?? new Set(chips.filter((c) => c.preselected).map((c) => c.id)),
    [pickedDrivers, chips],
  );
  const effectiveNets = useMemo(() => {
    if (pickedNets) return pickedNets;
    const d = defaultNetworkId(options);
    return new Set(d ? [d] : []);
  }, [pickedNets, options]);

  const [groups, setGroups] = useState<ScanGroup[] | null>(null);
  const [progress, setProgress] = useState<{ targets: ScanTarget[]; index: number; done: ScanGroup[] } | null>(null);
  const [cancelled, setCancelled] = useState(false);
  // Each scan (and a cancel) bumps the run; a loop that sees a newer run stops
  // and drops what it was waiting for.
  const runRef = useRef(0);
  const resultsRef = useRef<HTMLDivElement>(null);
  const scanning = progress !== null;

  const card = cards.find((c) => c.id === gatewayId) ?? null;
  const gatewayName = card?.name ?? "The gateway";
  const noGateway = isLiveApi && !gatewaysLoading && cards.length === 0;

  // What a scan would sweep, and whether it can be sent.
  const custom = checkScanTarget(range);
  const customActive = hasNetworks ? where === "custom" : true;
  const customBlankOnNetworks = hasNetworks && customActive && custom.ok && custom.cidr === "";
  const targets: ScanTarget[] = hasNetworks
    ? where === "networks"
      ? scanTargets(options, effectiveNets).map((o) => ({ cidr: o.cidr, label: o.label }))
      : custom.ok && custom.cidr !== ""
        ? [{ cidr: custom.cidr, label: custom.cidr }]
        : []
    : custom.ok
      ? [{ cidr: custom.cidr, label: custom.cidr || "the gateway's own network" }]
      : [];
  const rangeBlocked = customActive && !custom.ok;
  const noDriverPicked = chips.length > 0 && effectiveDrivers.size === 0;
  const probedIds = useMemo(
    () => (chips.length > 0 ? [...effectiveDrivers] : [DRIVER.MODBUS_TCP]),
    [chips.length, effectiveDrivers],
  );
  const portChecks = probedIds.map((id) => {
    const defaults = defaultScanPorts(id, drivers);
    return { id, check: checkScanPorts(portTexts[id] ?? formatScanPorts(defaults), defaults) };
  });
  const portBlocked = portChecks.some((p) => !p.check.ok);
  const canScan =
    isLiveApi && !!gatewayId && !scanning && targets.length > 0 && !rangeBlocked && !noDriverPicked && !portBlocked;

  const probedLabel = probedIds.map((id) => driverLabel(id, drivers)).join(", ");
  const enabledChips = chips.filter((c) => c.enabled);
  const summary = scanOptionsSummary({
    targets: hasNetworks
      ? where === "networks"
        ? scanTargets(options, effectiveNets).map(shortNetworkLabel)
        : targets.map((t) => t.cidr)
      : targets.map((t) => t.cidr || "the gateway's own network"),
    protocols: probedIds.map((id) => driverLabel(id, drivers)),
    allProtocols: enabledChips.length > 0 && enabledChips.every((c) => effectiveDrivers.has(c.id)),
    ports: portChecks.flatMap(({ id, check }) =>
      check.ok && !check.isDefault
        ? [`${driverLabel(id, drivers)} port${check.ports.length === 1 ? "" : "s"} ${check.ports.join(", ")}`]
        : [],
    ),
  });
  const whyNotScan = !isLiveApi
    ? null
    : !gatewayId
      ? "Pick a gateway first."
      : noDriverPicked
        ? "Tick at least one protocol in Scan options."
        : rangeBlocked
          ? "Fix the range in Scan options."
          : customBlankOnNetworks
            ? "Type a range in Scan options, or scan the gateway's network."
            : targets.length === 0
              ? "Tick a network in Scan options."
              : portBlocked
                ? "Fix the ports in Scan options."
                : null;

  const scan = async () => {
    if (!gatewayId || !canScan) return;
    // Scanning pins the gateway: a later pre-selection change must not move it.
    onGatewayId(gatewayId);
    const run = ++runRef.current;
    const driversParam = discoverDriversParam(effectiveDrivers);
    const { ports: portsParam, port: modbusPort } = discoverPortsParam(probedIds, portTexts, drivers);
    const out: ScanGroup[] = [];
    const list = [...targets];
    setGroups(null);
    setCancelled(false);
    // Fold the options away and bring the progress into view.
    setOptionsOpen(false);
    requestAnimationFrame(() => resultsRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" }));
    for (let i = 0; i < list.length; i++) {
      const t = list[i];
      setProgress({ targets: list, index: i, done: [...out] });
      try {
        const res = await gateway.discover(gatewayId, {
          cidr: t.cidr,
          drivers: driversParam,
          port: modbusPort,
          ports: portsParam,
        });
        if (runRef.current !== run) return;
        out.push({ cidr: t.cidr, label: t.label, devices: res.devices, partial: res.partial });
      } catch (e) {
        if (runRef.current !== run) return;
        out.push({
          cidr: t.cidr,
          label: t.label,
          devices: [],
          partial: false,
          error: scanErrorMessage(e, { op: "discover", gatewayName }),
        });
        // A refusal about the gateway or the caller fails every range alike: stop.
        const s = statusOf(e);
        if (s === 401 || s === 403 || s === 404 || s === 409 || s === 504 || s === undefined) break;
      }
    }
    setGroups(out);
    setProgress(null);
  };

  const cancel = () => {
    runRef.current++;
    setGroups(progress?.done ?? []);
    setProgress(null);
    setCancelled(true);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void scan();
  };

  // Results and picks belong to the gateway that produced them.
  const changeGateway = (id: string) => {
    if (id === gatewayId) return;
    runRef.current++;
    onGatewayId(id);
    setGroups(null);
    setProgress(null);
    setCancelled(false);
    setPickedDrivers(null);
    setPickedNets(null);
    setWhere("networks");
    setPortTexts({});
  };

  const toggle = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const found = groups?.reduce((n, g) => n + g.devices.length, 0) ?? 0;
  const selectedKey = selected ? deviceKey(selected) : null;
  const selectedInResults = !!selectedKey && !!groups?.some((g) => g.devices.some((d) => deviceKey(d) === selectedKey));

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Radar className="size-5 text-brand" />
            Find the device
          </CardTitle>
          <CardDescription>A gateway on the plant network scans for PLCs. Nothing is written.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {!isLiveApi ? (
            <EmptyState
              icon={Radar}
              title="Scanning needs the live API"
              description="This build has no backend to scan through. Add a device by hand below to explore the rest of the flow."
            />
          ) : noGateway ? (
            <EmptyState
              icon={Radar}
              title="No gateway to scan from"
              description="Scans run on a gateway on the plant network. Add one on the Gateways page first."
              cta={
                <Button asChild variant="outline">
                  <Link to="/app/gateways">Open Gateways</Link>
                </Button>
              }
            />
          ) : (
            <form className="flex flex-col gap-4" onSubmit={onSubmit}>
              <GatewayChooser
                cards={cards}
                loading={gatewaysLoading}
                value={gatewayId}
                obvious={gatewayObvious}
                disabled={scanning}
                onChange={changeGateway}
              />

              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" size="lg" disabled={!canScan}>
                  {scanning ? <Loader2 className="animate-spin" /> : <Radar />}
                  {scanning ? "Scanning…" : groups ? "Scan again" : "Scan for devices"}
                </Button>
                <span className="min-w-0 flex-1 text-xs text-on-surface-muted">
                  {whyNotScan ? (
                    <span className="text-warning">{whyNotScan}</span>
                  ) : (
                    <>
                      Scans <span className="text-on-surface">{summary}</span>, about{" "}
                      {SWEEP_BUDGET_S * Math.max(1, targets.length)} s.
                    </>
                  )}
                </span>
              </div>

              <Disclosure
                summary="Scan options"
                hint={optionsOpen ? undefined : summary}
                open={optionsOpen || rangeBlocked || portBlocked}
                onOpenChange={setOptionsOpen}
                variant="card"
                contentClassName="flex flex-col gap-4"
              >
                {chips.length > 0 && (
                  <ProtocolChips
                    chips={chips}
                    picked={effectiveDrivers}
                    disabled={scanning}
                    onToggle={(id) => setPickedDrivers(toggle(effectiveDrivers, id))}
                  />
                )}

                {hasNetworks ? (
                  <fieldset className="flex flex-col gap-1.5" disabled={scanning}>
                    <legend className="mb-1.5 text-sm font-medium text-on-surface">Where to scan</legend>
                    <RadioCardGroup
                      name="scan-where"
                      value={where}
                      onValueChange={(v) => setWhere(v as WhereMode)}
                      aria-label="Where to scan"
                    >
                      <RadioCard
                        value="networks"
                        title="Gateway network"
                        description={
                          options.length === 1
                            ? `${options[0].label}: ${scanEstimate(options[0].hosts)}`
                            : "The networks the gateway is on. Tick one or more; they are scanned one after another."
                        }
                      >
                        {options.length > 1 && (
                          <div className="flex flex-col gap-1.5">
                            {options.map((o) => (
                              <label key={o.id} className="flex items-start gap-2 text-sm text-on-surface">
                                <input
                                  type="checkbox"
                                  className="mt-1"
                                  aria-label={o.label}
                                  checked={effectiveNets.has(o.id)}
                                  onChange={() => setPickedNets(toggle(effectiveNets, o.id))}
                                />
                                <span className="flex flex-col">
                                  <span className={cn("font-mono text-xs", !o.up && "text-on-surface-muted")}>
                                    {o.label}
                                  </span>
                                  <span className="text-[11px] text-on-surface-muted">
                                    {scanEstimate(o.hosts)}
                                    {o.narrowed ? `; ${o.narrowed}` : ""}
                                  </span>
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </RadioCard>
                      <RadioCard
                        value="custom"
                        title="Custom range"
                        description="One address or a private range, up to a /20."
                      >
                        <div className="flex flex-col gap-1">
                          <Input
                            id={rangeId}
                            aria-label="Custom scan range"
                            value={range}
                            onChange={(e) => setRange(e.target.value)}
                            placeholder="e.g. 192.168.1.0/24 or 192.168.1.50"
                            className="max-w-72 font-mono"
                            aria-invalid={!custom.ok}
                          />
                          <RangeNote check={custom} emptyText="Type an address or a range." />
                        </div>
                      </RadioCard>
                    </RadioCardGroup>
                  </fieldset>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={rangeId}>Scan range (optional)</Label>
                    <Input
                      id={rangeId}
                      value={range}
                      onChange={(e) => setRange(e.target.value)}
                      placeholder="The gateway's own network, or e.g. 192.168.1.0/24"
                      disabled={scanning}
                      className="max-w-96 font-mono"
                      aria-invalid={!custom.ok}
                    />
                    <RangeNote check={custom} emptyText="Blank scans the gateway's own network." />
                  </div>
                )}

                <ScanPortsFields
                  probed={probedIds}
                  drivers={drivers}
                  texts={portTexts}
                  disabled={scanning}
                  onChange={(id, text) => setPortTexts((prev) => ({ ...prev, [id]: text }))}
                />
              </Disclosure>
            </form>
          )}

          <div ref={resultsRef} className="scroll-mb-24 empty:hidden">
            {progress ? (
              <ScanProgressList gatewayName={gatewayName} progress={progress} probed={probedLabel} onCancel={cancel} />
            ) : groups === null ? null : (
              <div className="flex flex-col gap-4">
                <div role="status" className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-on-surface">
                    {found === 0 ? "No device answered" : `${found} device${found === 1 ? "" : "s"} found`}
                  </span>
                  {cancelled && (
                    <span className="text-xs text-on-surface-muted">
                      Scan cancelled. The gateway finishes its current sweep (up to {SWEEP_BUDGET_S} s) before it can
                      scan again.
                    </span>
                  )}
                </div>
                {groups.map((g) => (
                  <section key={g.cidr || "own"} className="flex flex-col gap-2">
                    {groups.length > 1 && (
                      <h4 className="font-mono text-xs font-medium text-on-surface">
                        {g.label}
                        <span className="ml-2 font-sans font-normal text-on-surface-muted">
                          {g.error ? "failed" : `${g.devices.length} found`}
                        </span>
                      </h4>
                    )}
                    {g.error ? (
                      <ScanErrorNote message={g.error} onRetry={() => void scan()} retryDisabled={!canScan} />
                    ) : (
                      <>
                        {g.partial && (
                          <p
                            role="status"
                            className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-xs text-on-surface-muted"
                          >
                            The scan ran out of time, so this list may be incomplete. Scan a narrower range to be sure.
                          </p>
                        )}
                        {g.devices.length === 0 ? (
                          <p className="rounded-lg border border-dashed border-border p-4 text-sm text-on-surface-muted">
                            Nothing on {g.cidr || "the gateway's network"} answered on {probedLabel}. Check the device
                            is powered and on the same network as {gatewayName}, or add it by hand.
                          </p>
                        ) : (
                          <DeviceCards
                            devices={g.devices}
                            drivers={drivers}
                            selectedKey={selectedKey}
                            onPick={onPick}
                            onUse={onUse}
                          />
                        )}
                      </>
                    )}
                  </section>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {selected && !selectedInResults && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-brand/40 bg-brand-muted/30 px-4 py-3 text-sm">
          <Check className="size-4 text-brand" />
          <span className="font-medium text-on-surface">{selected.name ?? selected.ip}</span>
          <span className="font-mono text-[11px] text-on-surface-muted">
            {addressLabel(selected)} · {driverLabel(selected.driver, drivers)}
          </span>
          <Button size="sm" className="ml-auto" onClick={() => onUse(selected)}>
            Use this device
            <ArrowRight />
          </Button>
        </div>
      )}

      <ManualAdd drivers={drivers} networks={birth.networks} defaultOpen={!isLiveApi || noGateway} onPick={onPick} />
    </div>
  );
}

// ── gateway ─────────────────────────────────────────────────────────────────

/** The gateway to scan from: one line with "Change" when the pick is obvious, else cards. */
function GatewayChooser({
  cards,
  loading,
  value,
  obvious,
  disabled,
  onChange,
}: {
  cards: GatewayCard[];
  loading: boolean;
  value: string | null;
  obvious: boolean;
  disabled: boolean;
  onChange: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (loading && cards.length === 0) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading gateways">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }
  const current = cards.find((c) => c.id === value) ?? null;
  const collapsed = current && (cards.length === 1 || (obvious && !expanded));
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-on-surface">Scan from</span>
      {collapsed ? (
        <div className="flex items-center gap-3 rounded-lg border border-brand/40 bg-brand-muted/20 px-3 py-2.5">
          <GatewayStatusDot card={current} />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-on-surface">{current.name}</span>
            <span className="truncate text-xs text-on-surface-muted">{gatewayCardLine(current)}</span>
          </span>
          {current.multiProtocol && (
            <Badge variant="secondary" className="hidden text-[10px] sm:inline-flex">
              Multi-protocol
            </Badge>
          )}
          {cards.length > 1 && (
            <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => setExpanded(true)}>
              Change
            </Button>
          )}
        </div>
      ) : (
        <RadioCardGroup
          name="scan-gateway"
          value={value}
          onValueChange={(id) => {
            onChange(id);
            if (obvious) setExpanded(false);
          }}
          disabled={disabled}
          aria-label="Gateway to scan from"
          className="grid gap-2 sm:grid-cols-2"
        >
          {cards.map((c) => (
            <RadioCard
              key={c.id}
              value={c.id}
              title={c.name}
              description={gatewayCardLine(c)}
              aside={
                <>
                  {c.multiProtocol && (
                    <Badge variant="secondary" className="text-[10px]">
                      Multi-protocol
                    </Badge>
                  )}
                  <GatewayStatusDot card={c} />
                </>
              }
            />
          ))}
        </RadioCardGroup>
      )}
    </div>
  );
}

function GatewayStatusDot({ card }: { card: GatewayCard }) {
  const tone =
    card.status === "online"
      ? "bg-success"
      : card.status === "warning"
        ? "bg-warning"
        : card.status === "offline"
          ? "bg-danger"
          : "bg-on-surface-muted/50";
  return (
    <span
      role="img"
      aria-label={card.status === "online" ? "Online" : card.status === "offline" ? "Offline" : "Status unknown"}
      className={cn("size-2.5 shrink-0 rounded-full", tone)}
    />
  );
}

// ── results ─────────────────────────────────────────────────────────────────

/** Devices as radio cards; the picked one offers "Use this device". A filter shows past 8. */
function DeviceCards({
  devices,
  drivers,
  selectedKey,
  onPick,
  onUse,
}: {
  devices: DiscoveredDevice[];
  drivers: Parameters<typeof driverLabel>[1];
  selectedKey: string | null;
  onPick: (d: DiscoveredDevice) => void;
  onUse: (d: DiscoveredDevice) => void;
}) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? devices.filter((d) =>
        [d.name ?? "", d.ip, addressLabel(d), driverLabel(d.driver, drivers), identitySummary(d.identity, d.unit_id)]
          .join(" ")
          .toLowerCase()
          .includes(needle),
      )
    : devices;
  const byKey = new Map(devices.map((d) => [deviceKey(d), d]));
  return (
    <div className="flex flex-col gap-2">
      {devices.length > 8 && (
        <div className="relative max-w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-on-surface-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter devices…"
            aria-label="Filter devices"
            className="h-8 pl-8 text-xs"
            data-no-advance
          />
        </div>
      )}
      <RadioCardGroup
        name="scan-device"
        value={selectedKey}
        onValueChange={(k) => {
          const d = byKey.get(k);
          if (d) onPick(d);
        }}
        aria-label="Devices found"
      >
        {shown.map((d) => {
          const identity = identitySummary(d.identity, d.unit_id);
          return (
            <RadioCard
              key={deviceKey(d)}
              value={deviceKey(d)}
              title={d.name ?? d.ip}
              description={
                <span className="flex flex-col gap-0.5">
                  <span className="font-mono text-[11px]">{addressLabel(d)}</span>
                  {!d.online ? (
                    <span className="text-warning">Port open, but no Modbus reply</span>
                  ) : identity ? (
                    <span>{identity}</span>
                  ) : null}
                </span>
              }
              aside={
                <Badge variant="secondary" className="text-[10px]">
                  {driverLabel(d.driver, drivers)}
                </Badge>
              }
            >
              <Button type="button" size="sm" onClick={() => onUse(d)}>
                Use this device
                <ArrowRight />
              </Button>
            </RadioCard>
          );
        })}
      </RadioCardGroup>
      {needle && shown.length === 0 && <p className="text-xs text-on-surface-muted">No device matches “{q}”.</p>}
    </div>
  );
}

/** In-flight scan: each network done / scanning / waiting, and Cancel. */
function ScanProgressList({
  gatewayName,
  progress,
  probed,
  onCancel,
}: {
  gatewayName: string;
  progress: { targets: ScanTarget[]; index: number; done: ScanGroup[] };
  probed: string;
  onCancel: () => void;
}) {
  return (
    <div
      role="status"
      className="flex flex-col gap-3 rounded-lg border border-border bg-surface-elevated/40 p-4 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Loader2 className="size-4 shrink-0 animate-spin text-brand" />
        <span className="font-medium text-on-surface">
          Scanning from {gatewayName}
          {progress.targets.length > 1 ? ` (${progress.index + 1} of ${progress.targets.length})` : ""}…
        </span>
        <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={onCancel}>
          <CircleStop />
          Cancel
        </Button>
      </div>
      <ol className="flex flex-col gap-1.5">
        {progress.targets.map((t, i) => {
          const done = progress.done[i];
          const state = i < progress.index ? "done" : i === progress.index ? "scanning" : "waiting";
          return (
            <li key={`${t.cidr}#${i}`} className="flex items-center gap-2 text-xs">
              {state === "done" ? (
                done?.error ? (
                  <TriangleAlert className="size-3.5 text-danger" />
                ) : (
                  <Check className="size-3.5 text-success" />
                )
              ) : state === "scanning" ? (
                <Loader2 className="size-3.5 animate-spin text-brand" />
              ) : (
                <span className="size-3.5 rounded-full border border-border" aria-hidden />
              )}
              <span className="font-mono text-on-surface">{t.label}</span>
              <span className="text-on-surface-muted">
                {state === "done"
                  ? done?.error
                    ? "failed"
                    : `${done?.devices.length ?? 0} found`
                  : state === "scanning"
                    ? `probing for ${probed}, up to ${SWEEP_BUDGET_S} s`
                    : "waiting"}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ── options ─────────────────────────────────────────────────────────────────

/** The protocols to probe: one toggle per Ethernet driver; ones the gateway cannot run are off, with the reason. */
export function ProtocolChips({
  chips,
  picked,
  disabled,
  onToggle,
}: {
  chips: ReturnType<typeof discoverChips>;
  picked: ReadonlySet<string>;
  disabled?: boolean;
  onToggle: (id: string) => void;
}) {
  const notes = chips.filter((c) => c.note);
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-on-surface">Protocols to probe</span>
      <div role="group" aria-label="Protocols to probe" className="flex flex-wrap gap-1.5">
        {chips.map((c) => {
          const on = picked.has(c.id);
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={on}
              disabled={disabled || !c.enabled}
              title={c.note}
              onClick={() => onToggle(c.id)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                on
                  ? "border-brand bg-brand text-brand-foreground"
                  : "border-border bg-surface text-on-surface-muted hover:text-on-surface",
              )}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      {notes.length > 0 && (
        <ul className="text-[11px] text-on-surface-muted">
          {notes.map((c) => (
            <li key={c.id}>
              <span className="font-medium">{c.label}:</span> {c.note}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One "Ports" field per probed protocol, prefilled with the ports the gateway
 * probes by default. Comma-separated, 1 to 4 ports; only a changed list is sent.
 */
export function ScanPortsFields({
  probed,
  drivers,
  texts,
  disabled,
  onChange,
}: {
  probed: readonly string[];
  drivers: Parameters<typeof driverLabel>[1];
  /** As typed; a driver not in here shows its defaults. */
  texts: Readonly<Record<string, string>>;
  disabled?: boolean;
  onChange: (id: string, text: string) => void;
}) {
  const baseId = useId();
  if (probed.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-on-surface">Ports</span>
      <div className="flex flex-wrap gap-3">
        {probed.map((id) => {
          const defaults = defaultScanPorts(id, drivers);
          const text = texts[id] ?? formatScanPorts(defaults);
          const check = checkScanPorts(text, defaults);
          const fieldId = `${baseId}-${id}`;
          return (
            <div key={id} className="flex flex-col gap-1">
              <Label htmlFor={fieldId} className="text-xs font-normal text-on-surface-muted">
                {driverLabel(id, drivers)}
              </Label>
              <Input
                id={fieldId}
                value={text}
                onChange={(e) => onChange(id, e.target.value)}
                placeholder={formatScanPorts(defaults)}
                disabled={disabled}
                className={cn("h-8 font-mono text-xs", defaults.length > 1 ? "w-60" : "w-40")}
                aria-invalid={!check.ok}
                aria-describedby={check.ok ? undefined : `${fieldId}-error`}
              />
              {!check.ok && (
                <span id={`${fieldId}-error`} role="alert" className="max-w-60 text-[11px] text-danger">
                  {check.error}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <span className="text-[11px] text-on-surface-muted">
        Leave the defaults unless the device listens on another port.
      </span>
    </div>
  );
}

/** Under a range field: its problem, else its size and time, else what blank means. */
function RangeNote({ check, emptyText }: { check: ReturnType<typeof checkScanTarget>; emptyText: string }) {
  if (!check.ok) {
    return (
      <span role="alert" className="text-[11px] text-danger">
        {check.error}
      </span>
    );
  }
  return (
    <span className="flex flex-col text-[11px] text-on-surface-muted">
      <span>{check.cidr ? `${check.cidr}: ${scanEstimate(check.hosts)}.` : emptyText}</span>
      {check.warning && <span className="text-warning">{check.warning}</span>}
    </span>
  );
}

/** A failed scan, with the reason and a retry. */
function ScanErrorNote({
  message,
  onRetry,
  retryDisabled,
}: {
  message: string;
  onRetry: () => void;
  retryDisabled: boolean;
}) {
  return (
    <div role="alert" className="flex items-start gap-3 rounded-lg border border-danger/40 bg-danger/5 p-4 text-sm">
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-danger" />
      <div className="flex flex-col items-start gap-2">
        <span className="font-medium text-on-surface">Scan failed</span>
        <DeviceErrorText message={message} />
        <Button variant="outline" size="sm" onClick={onRetry} disabled={retryDisabled}>
          <Radar />
          Try again
        </Button>
      </div>
    </div>
  );
}

// ── manual ──────────────────────────────────────────────────────────────────

/** "Add a device by hand": a quiet link that expands to the manual form. */
function ManualAdd({
  drivers,
  networks,
  defaultOpen,
  onPick,
}: {
  drivers: Parameters<typeof driverLabel>[1];
  networks: ReturnType<typeof readBirth>["networks"];
  defaultOpen: boolean;
  onPick: (d: DiscoveredDevice) => void;
}) {
  const toast = useToast();
  const manualDriverId = useId();
  const manualIpId = useId();
  const manualPortId = useId();
  const manualUnitId = useId();

  const manualChoices = useMemo(() => {
    const list = (drivers ?? []).filter((d) => d.available && d.status !== "planned");
    return list.length > 0
      ? list.map((d) => ({ id: d.id, serial: d.medium === "serial" }))
      : [{ id: DRIVER.MODBUS_TCP, serial: false }];
  }, [drivers]);
  const [manualDriver, setManualDriver] = useState<string>(DRIVER.MODBUS_TCP);
  const manualDriverEff = manualChoices.some((c) => c.id === manualDriver) ? manualDriver : manualChoices[0].id;
  const manualSerial = manualChoices.find((c) => c.id === manualDriverEff)?.serial ?? false;
  const [manualIp, setManualIp] = useState("");
  const [manualPort, setManualPort] = useState("");
  const [manualUnit, setManualUnit] = useState("1");
  const manualWarning = manualSerial ? null : manualAddressWarning(manualIp, networks);

  const addManual = () => {
    const ip = manualIp.trim();
    if (!manualSerial && !ip) return;
    const modbus = isModbusDriver(manualDriverEff);
    const unit = manualUnit.trim() === "" ? 1 : Number(manualUnit);
    if (modbus && (!Number.isInteger(unit) || unit < 1 || unit > 247)) {
      toast.push("error", "Unit id must be a whole number from 1 to 247.");
      return;
    }
    const portText = manualPort.trim();
    const port = portText === "" ? undefined : Number(portText);
    if (port !== undefined && (!Number.isInteger(port) || port < 1 || port > 65535)) {
      toast.push("error", "Port must be a whole number from 1 to 65535.");
      return;
    }
    const d = manualDevice({ driver: manualDriverEff, host: ip, port, unitId: unit, serial: manualSerial });
    onPick(d);
    toast.push(
      "ok",
      manualSerial
        ? `Added a ${driverLabel(manualDriverEff, drivers)} device. Set its serial port on the next step.`
        : `Added ${ip}${modbus ? ` (unit ${unit})` : ""}. Its connection is checked on the next steps.`,
    );
  };

  return (
    <Disclosure
      summary="Add a device by hand"
      hint="for one the scan did not find"
      variant="link"
      defaultOpen={defaultOpen}
      className="px-1"
    >
      <div data-no-advance className="flex flex-col gap-2 rounded-lg border border-border bg-surface-elevated p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={manualDriverId}>Protocol</Label>
            <NativeSelect
              id={manualDriverId}
              value={manualDriverEff}
              onChange={(e) => {
                const next = e.target.value;
                setManualIp((cur) => manualHostAfterDriverChange(cur, manualDriverEff, next));
                setManualDriver(next);
                setManualPort("");
              }}
              className="h-9 w-40"
            >
              {manualChoices.map((c) => (
                <option key={c.id} value={c.id}>
                  {driverLabel(c.id, drivers)}
                </option>
              ))}
            </NativeSelect>
          </div>
          {!manualSerial && (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={manualIpId}>IP address</Label>
                <Input
                  id={manualIpId}
                  value={manualIp}
                  onChange={(e) => setManualIp(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManual();
                    }
                  }}
                  placeholder={driverDefaultHost(manualDriverEff) || "192.168.1.50"}
                  className="w-44 font-mono"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={manualPortId}>Port</Label>
                <Input
                  id={manualPortId}
                  type="number"
                  min={1}
                  max={65535}
                  value={manualPort}
                  onChange={(e) => setManualPort(e.target.value)}
                  placeholder={String(driverDefaultPort(manualDriverEff, drivers) ?? "")}
                  className="w-24"
                />
              </div>
            </>
          )}
          {isModbusDriver(manualDriverEff) && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={manualUnitId}>Unit id</Label>
              <Input
                id={manualUnitId}
                type="number"
                min={1}
                max={247}
                value={manualUnit}
                onChange={(e) => setManualUnit(e.target.value)}
                className="w-24"
              />
            </div>
          )}
          <Button variant="outline" onClick={addManual} disabled={!manualSerial && !manualIp.trim()}>
            <Plus />
            Add device
          </Button>
        </div>
        {manualWarning && (
          <p role="status" className="flex items-start gap-1.5 text-xs text-warning">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            {manualWarning}
          </p>
        )}
      </div>
    </Disclosure>
  );
}
